/**
 * Formattazione generica (numeri, valuta, date), indipendente dal dominio
 * del modulo. Le etichette specifiche (tipologie, stati, ecc.) restano nel
 * file utils/format.js di ciascuna app, che ri-esporta queste funzioni.
 */
import { getLang } from '../i18n/LanguageContext';
import { activeTimezone } from './kernelTimezone';

const FORMAT_LOCALES = ['it-IT', 'en-GB', 'en-US'];

/** Tag di formato (date, numeri) della lingua in uso: 25/10/2026 in it-IT e en-GB, 10/25/2026 in en-US. */
export const localeTag = () => (FORMAT_LOCALES.includes(getLang()) ? getLang() : 'it-IT');
const formatLocale = localeTag;

export const fmtNum = (value, digits = 0) => {
  const num = Number(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString(formatLocale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  });
};

export const fmtPct = (value, digits = 1) => {
  const num = Number(value);
  if (isNaN(num)) return '—';
  return fmtNum(num, digits) + '%';
};

export const fmtEuro = (value, digits = 0) => '€ ' + fmtNum(value, digits);

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DB_DATETIME = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

/**
 * Converte il valore ricevuto dall'API in una Date.
 * - "2026-10-25" (colonna DATE): giorno di calendario, senza fuso. Si costruisce a mezzogiorno locale,
 *   così nessun fuso lo sposta al giorno prima o dopo.
 * - "2026-10-25 06:30:00" / "2026-10-25T06:30:00" (DATETIME/TIMESTAMP): il database salva in UTC.
 * - Qualsiasi altra forma (ISO con Z o offset, timestamp) la interpreta il browser.
 */
export const parseDbDate = (value) => {
  if (value instanceof Date) return value;
  const s = String(value ?? '').trim();
  if (!s) return new Date(NaN);
  let m = DATE_ONLY.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  m = DB_DATETIME.exec(s);
  if (m) return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] || 0)));
  return new Date(s);
};

/** True se il valore è una data di calendario (senza orario): non va convertita di fuso. */
export const isDateOnly = (value) => DATE_ONLY.test(String(value ?? '').trim());

export const fmtDate = (value) => {
  if (!value) return '—';
  const d = parseDbDate(value);
  if (isNaN(d)) return String(value);
  // Una data di calendario resta quella; un istante si mostra nel giorno del fuso dell'utente (profilo o azienda).
  return isDateOnly(value) ? d.toLocaleDateString(formatLocale()) : d.toLocaleDateString(formatLocale(), { timeZone: activeTimezone() });
};

export const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = parseDbDate(value);
  if (isNaN(d)) return String(value);
  return d.toLocaleString(formatLocale(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: activeTimezone(),
  });
};
